// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
import { useEffect, useMemo, useState } from 'react'
import { db } from '../firebase/config'
import { useFirestoreGeneral } from '../firebase/useFirestore'
import { client } from './Client'
import { callFetchHierarchyData } from './useHierarchyData'
import useMsiMonetaryScores from './useMsiMonetaryScores'
import useInterventionDoseContext from './useInterventionDoseContext'
import buildMsiValueBreakdown from '../helpers/msiMonetisationHelpers/buildMsiValueBreakdown'
import { hasScoresFor } from '../helpers/meetstandaard/buildScoresFromStandaardDocument'
import msiValueExclusion from '../helpers/msiMonetisationHelpers/msiValueExclusion'
import getMatrixRowDefs from '../helpers/dataHelpers/matrixDataHelpers/getMatrixRowDefs'
import toMillis from '../helpers/dataHelpers/matrixDataHelpers/toMillis'
import notDeleted from '../helpers/notDeleted'
import defaultDates from '../utils/defaultDates'

// Fetch a few effects at a time, same pacing as DashboardMSIBenchmarksCard.
const REQUEST_BATCH_SIZE = 5

// Session caches shared across mounts (rowDefs mirror useMatrixData's cache).
const rowDefsCache = new Map()

// Records + rowDefs + scale for one MSI effect, merged over its indicator
// fields (AddEffectFlow creates one field per effect, but users can add more).
// Exported for useMsiEffectValue (per-effect €-line on the effect card).
//
// Fields are resolved via the EffectId link — the SAME link the analytics
// cards use (EffectVisualization), never via MSIId: older or re-added effects
// carry field docs without (or with a different) MSIId stamp — the
// meetstandaard API even lists duplicate effects with distinct ids — and the
// €-value must be computed from exactly the responses the effect chart shows.
// Only matrix fields count: a numeric non-matrix indicator (e.g. a 0–10
// scale) must not pollute the 1–5 level average.
export const fetchEffectData = async (effect, startDate, endDate) => {
    // Date bounds must reach the Cloud Function as MILLISECONDS (same as
    // useMatrixData → useHierarchyData). A raw Firestore Timestamp serializes
    // to a plain map and the server-side `Timestamp >=` comparison then
    // matches zero records — the €-value silently becomes 0 for everything.
    // AI-CONTEXT: See docs/pitfalls.md#api-callable-timestamp-vs-ms
    const startMs = toMillis(startDate) ?? 0
    const endMs = toMillis(endDate) ?? 8640000000000000

    const fieldsSnap = await db.collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', effect.ID)
        .where('CompagnyID', '==', client)
        .get()
    const fields = fieldsSnap.docs
        .map((d) => d.data())
        .filter(notDeleted)
        .filter((f) => f.ID && f.Type === 'matrix-one')

    const records = []
    const rowDefs = []
    for (const field of fields) {
        const [defs, data] = await Promise.all([
            getMatrixRowDefs(field.ID, db, rowDefsCache),
            callFetchHierarchyData({ syncType: 'MSIEffect', id: field.ID, startDate: startMs, endDate: endMs }),
        ])
        rowDefs.push(...defs)
        records.push(...(data.records || []))
    }

    return {
        records,
        rowDefs,
        lowerScale: fields[0]?.ReachStart ?? 1,
        upperScale: fields[0]?.ReachEnd ?? 5,
    }
}

/**
 * Tenant-wide MSI monetisation: fetches the response records of every MSI
 * effect (via the same fetchHierarchyData path the analytics cards use) and
 * folds them into euros with buildMsiValueBreakdown. Fail visibly: any fetch
 * error yields `total: null` — never a partial sum. A tenant without MSI
 * effects legitimately totals €0.
 *
 * Optioneel periode-gebonden (impact-per-euro-methodiek § 1, cohortprincipe):
 * geef `startMs`/`endMs` (epoch-ms) mee om de delta's binnen één boekjaar te
 * scopen. Zonder parameter blijft het gedrag ongewijzigd (all-time venster).
 * `linkedPersonaDeltaCount` telt de persona-gekoppelde delta's die doorgerekend
 * konden worden over alle niet-uitgesloten effecten — de MSI-bijdrage aan de ≥ 20-metingen-drempel (D5).
 *
 * `enabled: false` houdt de hook in laadstand zonder te fetchen. Consumers die
 * de periodegrenzen uit een nog ladende bron halen (de CompagnyCosts-rijen)
 * geven `enabled: costsLoaded` mee — anders start eerst een all-time sweep die
 * ms later door de echte jaargrenzen wordt weggegooid (de Cloud Function-calls
 * zijn dan al onderweg en tellen gewoon mee).
 *
 * Naast het bruto `total` komt de toegerekende waarde van de MSI-rekenregels
 * (`attributedTotal`, docs/features/msi-rekenregels-monetarisering.md) met de
 * telling van wie niet doorgerekend kon worden.
 *
 * @param {{ startMs?: number, endMs?: number, enabled?: boolean }} [period]
 * @returns {{ total: number | null, attributedTotal: number | null, notAttributedCount: number | null, notAttributedReasons: Object<string, number>, perEffect: Array<object>, linkedPersonaDeltaCount: number | null, loading: boolean, error: Error | null }}
 */
const useMsiMonetaryTotal = ({ startMs, endMs, enabled = true } = {}) => {
    const effects = useFirestoreGeneral('OutputEffects', 'Standard', 'true')
    const { scoresByMsiId, loading: scoresLoading, error: scoresError } = useMsiMonetaryScores()

    const [dataByEffectId, setDataByEffectId] = useState(/** @type {Object<string, object> | null} */ (null))
    const [recordsLoading, setRecordsLoading] = useState(true)
    const [recordsError, setRecordsError] = useState(/** @type {Error | null} */ (null))

    // Only identity fields drive refetching — the docs array is a new
    // reference on every snapshot.
    const effectsKey = JSON.stringify(
        (Array.isArray(effects) ? effects : []).map((e) => [e.ID, e.MSIId]),
    )

    // Effects that can ever produce MSI euros. A tenant without any (e.g. a
    // participation-only tenant, or one whose sole MSI effect is the excluded
    // Participatieladder) has a trivially €0 MSI stream — the scores fetch
    // must not block or fail their Maatschappelijke waarde.
    const relevantEffects = (Array.isArray(effects) ? effects : []).filter((e) =>
        e.ID && e.MSIId && !msiValueExclusion(e),
    )
    const hasRelevantEffects = relevantEffects.length > 0
    // Rekenregels MSI: deelnemers + standaard alleen lezen als er effecten zijn om door te rekenen.
    const dose = useInterventionDoseContext({ enabled: enabled && hasRelevantEffects })

    // Vóór de eerste OutputEffects-snapshot is `effects` nog geen array; dan is
    // "geen relevante effecten" niet te onderscheiden van "nog niet geladen" —
    // zonder deze vlag zou de hook heel even total: 0 als geladen presenteren.
    const effectsLoaded = Array.isArray(effects)

    useEffect(() => {
        if (!enabled || !effectsLoaded) return
        if (!hasRelevantEffects) {
            setDataByEffectId({})
            setRecordsLoading(false)
            setRecordsError(null)
            return
        }
        if (scoresLoading) return
        if (scoresError || !scoresByMsiId) {
            setDataByEffectId(null)
            setRecordsLoading(false)
            return
        }

        let cancelled = false
        const { defaultStartDate, defaultEndDate } = defaultDates()
        const periodStart = startMs ?? defaultStartDate
        const periodEnd = endMs ?? defaultEndDate

        // Records are only needed for effects that can produce euros —
        // excluded/noScores effects are listed by the breakdown without data.
        const toFetch = relevantEffects.filter((e) => hasScoresFor(scoresByMsiId, e.MSIId))

        const loadAll = async () => {
            setRecordsLoading(true)
            setRecordsError(null)

            const map = {}
            for (let i = 0; i < toFetch.length; i += REQUEST_BATCH_SIZE) {
                const batch = toFetch.slice(i, i + REQUEST_BATCH_SIZE)
                const results = await Promise.all(
                    batch.map((effect) => fetchEffectData(effect, periodStart, periodEnd)),
                )
                if (cancelled) return
                batch.forEach((effect, index) => { map[effect.ID] = results[index] })
            }

            if (!cancelled) {
                setDataByEffectId(map)
                setRecordsLoading(false)
            }
        }

        loadAll().catch((err) => {
            console.error('useMsiMonetaryTotal error:', err)
            if (!cancelled) {
                setRecordsError(err)
                setDataByEffectId(null)
                setRecordsLoading(false)
            }
        })

        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectsKey, scoresByMsiId, scoresLoading, scoresError, startMs, endMs, enabled, effectsLoaded])

    const breakdown = useMemo(() => {
        if (!enabled || !effectsLoaded) return null
        // Without euro-capable effects the scores map is irrelevant — build
        // against an empty map so excluded/no-MSIId effects are still listed.
        if (!hasRelevantEffects) {
            return buildMsiValueBreakdown({
                effects: Array.isArray(effects) ? effects : [],
                dataByEffectId: {},
                scoresByMsiId: scoresByMsiId || {},
            })
        }
        if (!scoresByMsiId || !dataByEffectId || !dose.context) return null
        return buildMsiValueBreakdown({
            effects: Array.isArray(effects) ? effects : [],
            dataByEffectId,
            scoresByMsiId,
            personasById: dose.context.personasById,
            orgDefault: dose.context.orgDefault,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectsKey, dataByEffectId, scoresByMsiId, hasRelevantEffects, enabled, effectsLoaded, dose.context])

    return {
        total: breakdown ? breakdown.total : null,
        attributedTotal: breakdown ? breakdown.attributedTotal : null,
        notAttributedCount: breakdown ? breakdown.notAttributedCount : null,
        notAttributedReasons: breakdown ? breakdown.notAttributedReasons : {},
        perEffect: breakdown ? breakdown.perEffect : [],
        linkedPersonaDeltaCount: breakdown
            ? breakdown.perEffect.reduce(
                // Alleen delta's die doorgerekend konden worden (rekenregels MSI):
                // een delta zonder horizon voedt de teller niet en mag de drempel
                // van twintig dus ook niet helpen halen.
                (count, entry) => count + (entry.excluded ? 0 : (entry.perPersona || []).filter((p) => p.attributed !== null).length),
                0,
            )
            : null,
        loading: (!enabled || !effectsLoaded)
            ? true
            : hasRelevantEffects ? (scoresLoading || recordsLoading || dose.loading) : false,
        error: hasRelevantEffects ? (scoresError || recordsError || dose.error) : null,
    }
}

export default useMsiMonetaryTotal
